import {
  Button,
  Buttons,
  CancelButton,
  Checkbox,
  Currency,
  CurrencyInput,
  DateTime,
  Drawer,
  Field,
  Form,
  FormMessage,
  HelpTooltip,
  Icon,
  Level,
  Radio,
  RadioGroup,
  Table,
} from '~/components';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useIntegrations, useToast, useWorkspace } from '~/contexts';
import { Formik } from 'formik';
import { useDirtyCheck, useDocumentTitle, useFeatures, useForm } from '~/hooks';
import { accountingProviders } from '~/lookups';
import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ErrorPage } from '~/routes/public/pages';
import styled from 'styled-components';
import { colors, weights } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';
import * as Yup from 'yup';
import InvoiceSyncIndicator from '~/routes/app/invoices/components/InvoiceSyncIndicator';

const Container = styled.div`
  font-size: 0.875rem;

  input:not([type='radio']) {
    font-size: inherit;
    width: 100%;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0; /* <-- Apparently some margin are still there even though it's hidden */
  }

  input[type='number'] {
    -moz-appearance: textfield; /* Firefox */
  }

  margin-bottom: 1rem;
`;

const SectionTitle = styled.div`
  width: 12rem;
  padding: 1rem 3rem 1rem 0;
  color: ${colors.grey40};
  font-size: 0.75rem;
  font-weight: ${weights.black};
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
`;

const TotalRow = styled(Table.Row)`
  border-bottom: none;
  font-weight: ${weights.bold};
  text-transform: uppercase;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  margin-right: 1rem;
`;

export default function PublishDialog({ creditNoteId, onClose, onSaved }) {
  useDocumentTitle('Publish Credit Note');

  const api = useApi();
  const { workspace } = useWorkspace();
  const features = useFeatures();
  const [{ creditNote, ...creditNoteQuery }, setCreditNote] = useState({ creditNote: null, isReady: false });
  const [invoices, setInvoices] = useState({ data: [], isReady: false });
  const [defaults, setDefaults] = useState({ data: [], isReady: false });
  const [{ status, message, isSubmitting }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);
  const toast = useToast();

  const integrations = useIntegrations();
  const [saveToAccounting, setSaveToAccounting] = useState(!!integrations.accounting);

  const fetchInvoices = useCallback(
    async ({ clientId, currency }) => {
      const { data } = await api.www.workspaces(workspace.id).creditNotes().findOpenInvoices({
        clientId,
        currency,
        creditNoteId,
      });

      setInvoices({ data, isReady: true });
      return data;
    },
    [api, workspace.id, creditNoteId],
  );

  const fetchData = useCallback(async () => {
    try {
      const { data: creditNote } = await api.www.workspaces(workspace.id).creditNotes(creditNoteId).get();
      setCreditNote({ creditNote, isReady: true });
      fetchInvoices({ clientId: creditNote.clientId, currency: creditNote.currency });

      const { data: defaults } = await api.www.workspaces(workspace.id).creditNotes(creditNoteId).defaultInvoices();
      setDefaults({ data: defaults, isReady: true });
    } catch (error) {
      toast.error('An error occurred while loading the credit note.');
      setCreditNote({ creditNote: null, isReady: true });
      onClose();
    }
  }, [workspace.id, creditNoteId, api, fetchInvoices, toast, onClose]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function handleClose() {
    onClose();
  }

  const creditNoteInvoices = useMemo(() => {
    if (!creditNote) return;
    if (!defaults.isReady) return;
    if (creditNote.invoices.length) return creditNote.invoices;

    return defaults.data;
  }, [creditNote, defaults]);

  if (!creditNoteQuery.isReady) return null;
  if (!creditNote) return <ErrorPage publicSite={false} />;
  if (!invoices.isReady || !defaults.isReady) return null;

  const calculateBalance = (invoiceId) => {
    const invoice = invoices.data.find((invoice) => invoice.id === invoiceId);

    let balance = invoice.balance;

    const applied = creditNote.invoices.find((i) => i.invoiceId === invoice.id);
    if (applied) balance += applied.amount;

    return balance;
  };

  function getAccountingIntegrationStatus(values) {
    if (!integrations.accounting || !invoices.isReady) return {};
    let accountingProvider = null;
    let accountingConnectionId = null;
    let accountingConnectionName = null;

    for (const cni of values.creditNoteInvoices) {
      const invoice = invoices.data.find((i) => i.id === cni.invoiceId);

      if (!invoice.accountingSynced) {
        const providerName = accountingProviders[invoice.accountingProvider]?.name;
        return {
          errorMessage: providerName
            ? `One or more invoices are not in ${providerName}.`
            : 'One or more invoices are not in an accounting system.',
        };
      }

      if (!accountingProvider) {
        accountingProvider = invoice.accountingProvider;
        accountingConnectionId = invoice.accountingConnectionId;
        accountingConnectionName = invoice.accountingConnectionName;
      } else if (accountingConnectionId !== invoice.accountingConnectionId) {
        const accountingProviderName = accountingProviders[accountingProvider].name;
        return {
          errorMessage: `The selected invoices are not related to the same ${accountingProviderName} integration.`,
        };
      }
    }

    if (!accountingProvider || !accountingConnectionId) return {};
    const accountingProviderName = accountingProviders[accountingProvider].name;
    if (!creditNote.client.accountingMappings?.some((m) => m.accountingConnectionId === accountingConnectionId)) {
      return {
        accountingProvider,
        accountingConnectionName,
        errorMessage: `The selected client is not mapped to a ${accountingProviderName} ${accountingProvider === 'xero' ? 'Contact' : 'Customer'}.`,
      };
    }
    return { accountingProvider, accountingConnectionName };
  }

  const getInvoiceIntegration = (invoice) => {
    if (invoice.accountingSynced && invoice.accountingProvider && invoice.accountingConnectionId) {
      return { type: invoice.accountingProvider, id: invoice.accountingConnectionId };
    }

    return { type: 'none' };
  };

  const getSelectedIntegration = (values) => {
    const selectedInvoice = values.creditNoteInvoices.find((cni) => Number(cni.amount) > 0);
    if (!selectedInvoice) return null;

    const selectedInvoiceData = invoices.data.find((invoice) => invoice.id === selectedInvoice.invoiceId);
    return selectedInvoiceData ? getInvoiceIntegration(selectedInvoiceData) : null;
  };

  const isSameIntegration = (selectedIntegration, invoice) => {
    if (!selectedIntegration) return true;

    const invoiceIntegration = getInvoiceIntegration(invoice);

    if (selectedIntegration.type === 'none') {
      return invoiceIntegration.type === 'none';
    }

    return invoiceIntegration.type === selectedIntegration.type && invoiceIntegration.id === selectedIntegration.id;
  };

  const unadjustableLines = [];
  creditNote.lines.forEach((line, index) => {
    if (!Number(line.amount)) return;

    const reasons = [];
    if (!line.projectId) reasons.push('no project');
    if (!line.revenueType) reasons.push('no revenue type');
    if (line.project && line.project.currency !== creditNote.currency)
      reasons.push(`a project in ${line.project.currency}`);

    if (reasons.length > 0) unadjustableLines.push({ line, number: index + 1, reasons });
  });

  const initialValues = mergeValues({
    adjustEarnedRevenue: !!creditNote.adjustEarnedRevenue,
    creditNoteInvoices,
  });

  const schema = Yup.object()
    .shape({
      creditNoteInvoices: Yup.array().of(
        Yup.object().shape({
          amount: Yup.number()
            .label('Amount')
            .min(0)
            .max(99999999999)
            .nullable()
            .test('amount', function (value) {
              const balance = calculateBalance(this.parent.invoiceId);

              if (balance < value)
                return this.createError({
                  message: 'The applied credit cannot exceed the invoice balance.',
                });

              return value;
            }),
        }),
      ),
    })
    .test('total', function (values) {
      if (creditNote.total !== _.round(_.sumBy(values.creditNoteInvoices, 'amount'), 2))
        return this.createError({
          path: 'total',
          message: 'The applied credit must match the total credit.',
        });

      return true;
    });

  return (
    <Drawer
      isOpen
      title="Publish Credit Note"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSubmit = async (values) => {
          try {
            form.submit();
            let result;

            const { accountingProvider, errorMessage } = getAccountingIntegrationStatus(values);
            const shouldTargetPayment = accountingProvider === 'qbo' || accountingProvider === 'sage';

            const body = emptyStringToNull({
              ...values,
              target: shouldTargetPayment ? 'payment' : 'invoice',
            });

            const { data } = await api.www.workspaces(workspace.id).creditNotes(creditNote.id).publish(body);
            result = data;

            if (accountingProvider && saveToAccounting && !errorMessage) {
              const accountingProviderName = accountingProviders[accountingProvider].name;

              try {
                const { data } = await api.www
                  .workspaces(workspace.id)
                  .creditNotes(result.id)
                  .saveToAccountingIntegration({ accountingProvider });
                result = data.creditNote;
              } catch (error) {
                toast.error(
                  error.message ??
                    `An error has occurred. The credit note has not been saved to ${accountingProviderName}. Please review the ${accountingProviderName} integration settings and try again.`,
                );
              }
            }

            form.save();
            closeDrawer();
            if (onSaved) onSaved(result);
            toast.success(`Credit Note has been published with number #${result.number}.`);
          } catch (error) {
            form.error(error);
          }
        };

        return (
          <Formik
            innerRef={formRef}
            initialValues={initialValues}
            validateOnBlur={false}
            validateOnChange={false}
            onSubmit={handleSubmit}
            validationSchema={schema}>
            {(formik) => {
              const total = _.round(
                _.sumBy(formik.values.creditNoteInvoices, (cni) => Number(cni.amount) || 0),
                2,
              );

              const { accountingProvider, accountingConnectionName, errorMessage } = getAccountingIntegrationStatus(
                formik.values,
              );
              const selectedIntegration = getSelectedIntegration(formik.values);

              const currency = creditNote.currency;

              return (
                <Form>
                  <Form.Control>
                    <ReadTextbox label="Client" value={creditNote.client.name} />
                    <ReadTextbox
                      label="Total Credit"
                      value={
                        <p style={{ textAlign: 'right' }}>
                          <Currency value={creditNote.total} currency={currency} />
                        </p>
                      }
                    />
                  </Form.Control>

                  <Container>
                    <SectionTitle>Earned Revenue</SectionTitle>

                    <RadioGroup
                      name="adjustEarnedRevenue"
                      direction="vertical"
                      value={formik.values.adjustEarnedRevenue ? 'adjust' : 'keep'}
                      onChange={(value) => formik.setFieldValue('adjustEarnedRevenue', value === 'adjust')}>
                      <Radio value="keep" label="Do not adjust the project's earned revenue" />
                      <Radio
                        value="adjust"
                        label={
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            Adjust the earned revenue by the credit note amount
                            <HelpTooltip
                              style={{ marginLeft: '0.5rem' }}
                              message="A negative pre-tax earned revenue adjustment will be created as of the credit note's issue date."
                            />
                          </div>
                        }
                      />
                    </RadioGroup>

                    {formik.values.adjustEarnedRevenue && unadjustableLines.length > 0 && (
                      <FormMessage.Error spaceTop>
                        To adjust the earned revenue, update these lines first:
                        <ul>
                          {unadjustableLines.map(({ line, number, reasons }) => (
                            <li key={line.id}>
                              Line {number}
                              {line.invoiceItem && ` (${line.invoiceItem.name})`}: {reasons.join(', ')}
                            </li>
                          ))}
                        </ul>
                      </FormMessage.Error>
                    )}
                  </Container>

                  <Container>
                    <SectionTitle>Open Invoices</SectionTitle>

                    {invoices.data.length === 0 ? (
                      `There are no open invoices for the selected ${features.purchaseOrders ? 'purchase order, client and currency' : 'client and currency'}.`
                    ) : (
                      <Table>
                        <Table.BoxHeader>
                          <Table.Column width="2.5rem" />
                          <Table.Column>Invoice #</Table.Column>
                          <Table.Column width="7rem">Due Date</Table.Column>
                          <Table.Column width="7.5rem" align="right">
                            Original Amount
                          </Table.Column>
                          <Table.Column width="7.5rem" align="right">
                            Open Balance
                          </Table.Column>
                          <Table.Column align="right">Credit</Table.Column>
                        </Table.BoxHeader>

                        <Table.Body>
                          {invoices.data.map((invoice) => {
                            const index = _.findIndex(
                              formik.values.creditNoteInvoices,
                              (cni) => cni.invoiceId === invoice.id,
                            );
                            const creditNoteInvoice = formik.values.creditNoteInvoices[index];
                            const amount = creditNoteInvoice?.amount || '';

                            const balance = calculateBalance(invoice.id);

                            const errors = formik.errors.creditNoteInvoices
                              ? formik.errors.creditNoteInvoices[index]
                              : {};

                            const handleAmountChange = (value) => {
                              let creditNoteInvoices = [...formik.values.creditNoteInvoices];

                              if (!value || Number(value) === 0) {
                                creditNoteInvoices = creditNoteInvoices.filter((cni) => cni.invoiceId !== invoice.id);
                              } else {
                                creditNoteInvoices = creditNoteInvoice
                                  ? creditNoteInvoices.map((cni) =>
                                      cni.invoiceId === invoice.id ? { ...cni, amount: value } : cni,
                                    )
                                  : [...creditNoteInvoices, { invoiceId: invoice.id, amount: value }];
                              }

                              formik.setValues({ ...formik.values, creditNoteInvoices });
                            };

                            const handleAmountBlur = () => {
                              formik.setFieldTouched('creditNoteInvoices', true, false);

                              if (formik.errors.total || errors?.amount) formik.validateForm();
                            };

                            const isDisabled = !isSameIntegration(selectedIntegration, invoice) && !creditNoteInvoice;

                            return (
                              <Table.BoxRow key={invoice.id} isDisabled={isDisabled}>
                                <Table.Cell>
                                  {!!creditNoteInvoice && <Icon icon="check" color={colors.success} />}
                                </Table.Cell>

                                <Table.Cell>
                                  #{invoice.number}
                                  {invoice.accountingSynced && (
                                    <span>
                                      <InvoiceSyncIndicator invoice={invoice} />
                                    </span>
                                  )}
                                </Table.Cell>

                                <Table.Cell>
                                  <DateTime value={invoice.dueOn} />
                                </Table.Cell>

                                <Table.Cell>
                                  <Currency value={invoice.total} currency={currency} />
                                </Table.Cell>

                                <Table.Cell>
                                  <Currency value={balance} currency={currency} />
                                </Table.Cell>

                                <Table.Cell>
                                  <Field.Control error={errors?.amount}>
                                    <CurrencyInput
                                      value={amount}
                                      currency={currency}
                                      disabled={isDisabled}
                                      onChange={handleAmountChange}
                                      onBlur={handleAmountBlur}
                                    />
                                  </Field.Control>
                                </Table.Cell>
                              </Table.BoxRow>
                            );
                          })}

                          <TotalRow>
                            <Table.Cell />
                            <Table.Cell>Total</Table.Cell>
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell />
                            <Table.Cell>
                              <Field.Control error={formik.errors.total} style={{ alignItems: 'center' }}>
                                <Currency value={total} currency={currency} />
                              </Field.Control>
                            </Table.Cell>
                          </TotalRow>
                        </Table.Body>
                      </Table>
                    )}
                  </Container>

                  {status && <FormMessage.Error>{message}</FormMessage.Error>}

                  <Drawer.Actions>
                    <Level>
                      {accountingProvider && !errorMessage && integrations[accountingProvider] && (
                        <Level.Item>
                          <CheckboxContainer>
                            <Checkbox
                              label={`Save to ${accountingProviders[accountingProvider].name}`}
                              subText={accountingConnectionName}
                              checked={saveToAccounting}
                              onChange={() => setSaveToAccounting((prev) => !prev)}
                            />
                          </CheckboxContainer>
                        </Level.Item>
                      )}
                    </Level>
                    <Buttons align="right">
                      <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                      <Button
                        type="submit"
                        disabled={
                          invoices.data.length === 0 ||
                          (formik.values.adjustEarnedRevenue && unadjustableLines.length > 0)
                        }
                        isLoading={isSubmitting}>
                        Publish
                      </Button>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}
